import type { Config } from "wagmi"
import { sendTransaction, signTypedData, waitForTransactionReceipt } from "@wagmi/core"
import { runInAction } from "mobx"
import { createActor, fromPromise, setup } from "xstate"

import type { ZapState } from "@/domains/minimal-pools-zap/zap-store"
import type { PortalsApprovalRequest, PortalsEstimateRequest } from "@/domains/portals/types"
import type { WalletConnectionStore } from "@/domains/wallet"
import { ZapStore } from "@/domains/minimal-pools-zap/zap-store"
import { PortalsApiTransport } from "@/domains/portals/portals-api-transport"

interface ZapOrchestratorContext {
  chainId: number
  store: ZapStore
  portalsTransport: PortalsApiTransport
  config: Config
  walletConnection: WalletConnectionStore
}

export class ZapOrchestrator {
  public readonly store: ZapStore

  private actor: ReturnType<typeof createActor>

  constructor(
    chainId: number,
    store: ZapStore,
    portalsTransport: PortalsApiTransport,
    config: Config,
    walletConnection: WalletConnectionStore,
  ) {
    this.store = store

    const context: ZapOrchestratorContext = {
      chainId,
      store,
      portalsTransport,
      config,
      walletConnection,
    }

    const machine = createZapMachine(context)
    this.actor = createActor(machine)

    // Sync XState state with MobX store
    this.actor.subscribe((snapshot) => {
      runInAction(() => {
        // Debug: Log actor state changes
        // console.log("this.actor", this.actor)

        this.store.setState(snapshot.value as ZapState)
      })
    })

    // Start the actor
    this.actor.start()
  }

  // Public API methods

  startZapFlow(): void {
    this.actor.send({ type: "START_ZAP" })
  }

  retryEstimate(): void {
    this.store.clearEstimateError()
    this.actor.send({ type: "RETRY_ESTIMATE" })
  }

  backToIdle(): void {
    this.actor.send({ type: "BACK_TO_IDLE" })
  }

  executeZap(): void {
    this.actor.send({ type: "EXECUTE_ZAP" })
  }

  approveZap(): void {
    this.actor.send({ type: "APPROVE_ZAP" })
  }

  retryApproval(): void {
    this.store.clearApprovalError()
    this.actor.send({ type: "RETRY_APPROVAL" })
  }

  retryExecution(): void {
    this.store.clearExecutionError()
    this.actor.send({ type: "RETRY_EXECUTION" })
  }

  cancel(): void {
    this.store.reset()
    this.actor.send({ type: "CANCEL" })
  }

  reset(): void {
    this.store.reset()
    this.actor.send({ type: "RESET" })
  }
}

// XState v5 Machine Definition
function createZapMachine(context: ZapOrchestratorContext) {
  return setup({
    actors: {
      getEstimate: fromPromise(async () => {
        if (!context.store.pool || !context.store.zapToken) {
          throw new Error("Pool and zap token must be set")
        }

        const request: PortalsEstimateRequest = {
          inputToken: context.store.zapToken.address.toPortalsAddress(context.chainId).toString(),
          inputAmount: context.store.inputAmountBigint.toString(),
          outputToken: context.store.pool.address.toPortalsAddress(context.chainId).toString(),
        }

        const estimate = await context.portalsTransport.estimate(request)

        // Validate estimate response
        if (!estimate || !estimate.outputAmount || !estimate.minOutputAmount) {
          throw new Error("Invalid estimate response from Portals API")
        }

        runInAction(() => {
          context.store.updateEstimate(estimate)
        })

        return estimate // Return the data for XState
      }),

      checkApproval: fromPromise(async () => {
        if (!context.store.zapToken) {
          throw new Error("Zap token must be set")
        }

        const connectedAddress = context.walletConnection.connectedAddress?.toString()
        if (!connectedAddress) {
          throw new Error("No wallet connected")
        }

        const request: PortalsApprovalRequest = {
          sender: connectedAddress,
          inputToken: `base:${context.store.zapToken.address.toString()}`,
          inputAmount: context.store.inputAmountBigint.toString(),
        }

        const approvalResponse = await context.portalsTransport.checkApproval(request)

        runInAction(() => {
          context.store.updateApprovalResponse(approvalResponse)
        })

        // Add a small delay to allow UI updates
        await new Promise((resolve) => setTimeout(resolve, 500))
        return approvalResponse
      }),

      executeApproval: fromPromise(async () => {
        const approvalResponse = context.store.approvalResponse
        if (!approvalResponse?.approve) {
          throw new Error("No approval transaction data available")
        }

        const txHash = await sendTransaction(context.config, {
          to: approvalResponse.approve.to as `0x${string}`,
          data: approvalResponse.approve.data as `0x${string}`,
          value: 0n,
          gas: BigInt(approvalResponse.approve.gasLimit),
        })

        runInAction(() => {
          context.store.setApprovalTxHash(txHash)
        })

        // Wait for approval confirmation
        const receipt = await waitForTransactionReceipt(context.config, { hash: txHash })
        console.log("approval receipt", receipt)

        runInAction(() => {
          context.store.setApprovalTxHash(receipt.transactionHash)
        })
      }),

      executePermit: fromPromise(async () => {
        const approvalResponse = context.store.approvalResponse
        if (!approvalResponse?.permit) {
          throw new Error("No permit data available")
        }

        const signature = await signTypedData(context.config, {
          domain: {
            verifyingContract: approvalResponse.permit.domain.verifyingContract as `0x${string}`,
            chainId: approvalResponse.permit.domain.chainId,
            name: approvalResponse.permit.domain.name,
            version: approvalResponse.permit.domain.version,
          },
          types: approvalResponse.permit.types,
          primaryType: "Permit",
          message: approvalResponse.permit.value,
        })
        console.log("signature", signature)
      }),

      executeZap: fromPromise(async () => {
        if (!context.store.estimate) {
          throw new Error("No estimate available")
        }

        const connectedAddress = context.walletConnection.connectedAddress?.toString()
        if (!connectedAddress) {
          throw new Error("No wallet connected")
        }

        const request = {
          inputToken: `base:${context.store.zapToken!.address.toString()}`,
          inputAmount: context.store.inputAmountBigint.toString(),
          outputToken: `base:${context.store.pool!.address.toString()}`,
          sender: connectedAddress,
        }

        const executionResponse = await context.portalsTransport.execute(request)
        runInAction(() => {
          context.store.updateExecutionResponse(executionResponse)
        })

        // Execute the transaction
        const txHash = await sendTransaction(context.config, {
          to: executionResponse.tx.to as `0x${string}`,
          data: executionResponse.tx.data as `0x${string}`,
          value: BigInt(executionResponse.tx.value),
        })

        runInAction(() => {
          context.store.setExecutionTxHash(txHash)
        })

        // Wait for approval confirmation
        const receipt = await waitForTransactionReceipt(context.config, { hash: txHash })
        console.log("execution receipt", receipt)

        runInAction(() => {
          context.store.setExecutionTxHash(receipt.transactionHash)
        })
      }),
    },
    guards: {
      noApprovalNeeded: () => {
        return !context.store.approvalResponse?.context.shouldApprove
      },
      approvalNeeded: () => {
        return context.store.approvalResponse?.context.shouldApprove ?? false
      },
    },
  }).createMachine({
    id: "zap",
    initial: "idle",
    on: {
      BACK_TO_IDLE: ".idle",
    },
    states: {
      idle: {
        on: {
          START_ZAP: "gettingEstimate",
        },
      },
      gettingEstimate: {
        invoke: {
          src: "getEstimate",
          onDone: "estimated",
          onError: {
            target: "estimateFailed",
            actions: ({ event }) => {
              runInAction(() => {
                // Debug: Log error event details
                // console.log("context", context)
                // console.log("getEstimate -> event", event)

                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Estimate failed"

                // console.log("errorMessage", errorMessage)
                switch (errorMessage) {
                  case "inputToken not found":
                    context.store.setEstimateError("Input token not supported")
                    break
                  default:
                    context.store.setEstimateError(errorMessage)
                }
              })
            },
          },
        },
      },
      estimated: {
        invoke: {
          src: "checkApproval",
          onDone: [
            {
              target: "approved",
              guard: "noApprovalNeeded",
            },
            {
              target: "approvalRequired",
              guard: "approvalNeeded",
            },
          ],
          onError: {
            target: "estimateFailed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Approval check failed"

                switch (errorMessage) {
                  case "inputToken not found":
                    context.store.setEstimateError("Input token not found")
                    break
                  default:
                    context.store.setEstimateError("Approval check failed")
                }
              })
            },
          },
        },
        on: {
          EXECUTE_ZAP: "executing",
          RETRY_ESTIMATE: "gettingEstimate",
          CANCEL: "idle",
        },
      },
      // Error states with retry capabilities
      estimateFailed: {
        on: {
          RETRY_ESTIMATE: "gettingEstimate",
          CANCEL: "idle",
        },
      },
      approvalRequired: {
        on: {
          APPROVE_ZAP: "approving",
          CANCEL: "idle",
        },
      },
      approving: {
        invoke: {
          src: "executeApproval",
          onDone: "approved",
          onError: {
            target: "approvalFailed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Approval transaction failed"
                context.store.setApprovalError(errorMessage)
              })
            },
          },
        },
        on: {
          CANCEL: "idle",
        },
      },
      approved: {
        on: {
          EXECUTE_ZAP: "executing",
          CANCEL: "idle",
        },
      },
      executing: {
        invoke: {
          src: "executeZap",
          onDone: "completed",
          onError: {
            target: "executionFailed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Zap execution failed"
                context.store.setExecutionError(errorMessage)
              })
            },
          },
        },
      },
      completed: {
        on: {
          RESET: "idle",
        },
      },

      approvalFailed: {
        on: {
          RETRY_APPROVAL: "approving",
          CANCEL: "idle",
        },
      },
      executionFailed: {
        on: {
          RETRY_EXECUTION: "executing",
          CANCEL: "idle",
        },
      },
    },
  })
}
